import { Trans } from '@lingui/macro'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { Box, Button } from '@mui/material'
import { FrameworkComponentProps } from '@om1/platform-utils'
import React, { useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { cohortBlocksEditActions, CohortTreeActionType, CriteriaType, OperationNode } from '../../../state'
import { CRITERIA_DEFAULTS } from '../base/CohortEditComponent'
import { useCriteriaDropZone } from '../utils/useCriteriaDropZone'
import { CriteriaSelectionMenu } from './CriteriaSelectionMenu'

export type EmptyDropZoneProps = FrameworkComponentProps<{ node: OperationNode }, typeof cohortBlocksEditActions, { showCriteriaSelection?: boolean }>

/**
 * An always-present drop zone displayed in a criteria block when it is empty.
 */
export const EmptyDropZone: React.FunctionComponent<EmptyDropZoneProps> = ({ state, actions, props: { showCriteriaSelection = false } }) => {
    const ref = useRef<HTMLDivElement>(null)
    const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null)
    const menuOpen = Boolean(menuAnchor)

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget)
    const handleMenuClose = () => setMenuAnchor(null)

    const { dropProps, isDropTarget } = useCriteriaDropZone({
        ref,
        onAdd: (type) => {
            const uuid = uuidv4()
            if (Object.keys(CRITERIA_DEFAULTS).includes(type)) {
                actions.criteriaInsert({ target: { nodeId: state.node.id }, criteriaType: type, filters: CRITERIA_DEFAULTS[type], uuid })
            } else {
                actions.criteriaDialogTrigger({ type, action: { type: CohortTreeActionType.Insert, target: { nodeId: state.node.id }, uuid } })
            }
        },
        onCopy: (source) => actions.criteriaCopy({ source, target: { nodeId: state.node.id } }),
        onMove: (source) => actions.criteriaMove({ source, target: { nodeId: state.node.id } })
    })

    const handleAddCriteria = (criteriaType: CriteriaType) => {
        const uuid = uuidv4()
        if (Object.keys(CRITERIA_DEFAULTS).includes(criteriaType)) {
            actions.criteriaInsert({ target: { nodeId: state.node.id }, criteriaType, filters: CRITERIA_DEFAULTS[criteriaType], uuid })
        } else {
            actions.criteriaDialogTrigger({
                type: criteriaType,
                action: { type: CohortTreeActionType.Insert, target: { nodeId: state.node.id }, uuid }
            })
        }
    }

    return (
        <>
            <Box display='flex' alignItems='center' gap={1} width='100%'>
                {showCriteriaSelection && (
                    <Button
                        variant='contained'
                        onClick={handleMenuOpen}
                        data-testid='criteria-block-add-criteria'
                        startIcon={<ArrowDropDownIcon sx={{ fontSize: '24px', color: 'white' }} />}
                        sx={{
                            minWidth: '150px',
                            justifyContent: 'flex-start',
                            height: '60px',
                            alignItems: 'center',
                            borderRadius: '8px',
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark'
                            }
                        }}
                    >
                        <span style={{ fontSize: '15px', textTransform: 'none' }}>
                            <Trans>Select Criteria</Trans>
                        </span>
                    </Button>
                )}
                <Box
                    // Drag and drop
                    ref={ref}
                    role='button'
                    tabIndex={0}
                    {...dropProps}
                    // Styles
                    p={2}
                    border={2}
                    borderColor='primary.light'
                    bgcolor={isDropTarget ? 'grey.200' : 'transparent'}
                    textAlign='center'
                    flex={1}
                    sx={{ borderStyle: 'dashed' }}
                >
                    <Trans>Drag and drop here to add criteria</Trans>
                </Box>
            </Box>

            <CriteriaSelectionMenu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                onCriteriaSelect={handleAddCriteria}
                testIdPrefix='criteria-block-menu-item'
            />
        </>
    )
}
